import { Scale, Phone, Gavel, FileText, Shield, AlertTriangle } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const TaxLitigation = () => {
  return (
    <GenericPracticePage
      title="Tax Litigation & IRS Defense"
      subtitle="Experienced representation in IRS audits, tax disputes, and Tax Court proceedings. We defend taxpayers against aggressive IRS collection actions and fight for fair resolutions."
      icon={<Scale className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">IRS Audit & Tax Litigation Defense</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Facing an IRS audit or tax dispute is stressful and intimidating. The IRS has vast resources and experienced agents working against you. You need an attorney who understands tax law, IRS procedures, and how to negotiate favorable outcomes with federal and state tax authorities.
            </p>
            <p className="text-lg text-muted-foreground">
              Whether you're dealing with an audit, collections, or need to appeal an IRS decision, we provide aggressive representation to protect your rights and minimize your tax liability.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Tax Litigation Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <FileText className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">IRS Audits</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Representation throughout the audit process, from initial contact through resolution.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Correspondence audits</li>
                  <li>• Office examinations</li>
                  <li>• Field audits at business or home</li>
                  <li>• Document preparation and response</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Gavel className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Tax Court Representation</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Experienced litigation in U.S. Tax Court when negotiations fail.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Petitions and motions</li>
                  <li>• Discovery and evidence</li>
                  <li>• Settlement conferences</li>
                  <li>• Trial preparation and representation</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Scale className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">IRS Appeals</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Challenge IRS determinations through the administrative appeals process.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Appeals conferences</li>
                  <li>• Protest letters</li>
                  <li>• Settlement negotiations</li>
                  <li>• Collection Due Process hearings</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Shield className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Collections Defense</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Stop aggressive IRS collection actions including levies and liens.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Stop wage garnishments</li>
                  <li>• Release bank levies</li>
                  <li>• Remove tax liens</li>
                  <li>• Prevent asset seizure</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Common IRS Audit Triggers</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg space-y-3">
              <p className="text-muted-foreground">• <strong>High Income:</strong> Returns with income over $200,000 face higher audit rates</p>
              <p className="text-muted-foreground">• <strong>Business Deductions:</strong> Schedule C filers claiming large deductions relative to income</p>
              <p className="text-muted-foreground">• <strong>Home Office:</strong> Home office deductions are frequently scrutinized</p>
              <p className="text-muted-foreground">• <strong>Vehicle Expenses:</strong> 100% business use of vehicles often raises red flags</p>
              <p className="text-muted-foreground">• <strong>Cash Businesses:</strong> Restaurants, retail, and cash-intensive businesses face more audits</p>
              <p className="text-muted-foreground">• <strong>Math Errors:</strong> Simple mistakes can trigger examination</p>
              <p className="text-muted-foreground">• <strong>Round Numbers:</strong> Excessive rounding suggests estimates rather than records</p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Our Tax Litigation Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Initial Assessment</h3>
                  <p className="text-sm text-muted-foreground">We review all IRS correspondence, your returns, and supporting documents to understand the situation</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Strategic Planning</h3>
                  <p className="text-sm text-muted-foreground">Develop a defense strategy: challenge the audit, negotiate settlement, or prepare for appeals/court</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Direct Communication</h3>
                  <p className="text-sm text-muted-foreground">We handle all communications with the IRS - you don't have to speak to agents directly</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">Negotiation & Resolution</h3>
                  <p className="text-sm text-muted-foreground">Negotiate the best possible outcome: reduced assessment, payment plan, or case dismissal</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Your Rights During an IRS Audit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Right to professional representation</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Right to know why IRS is contacting you</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Right to appeal IRS decisions</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Right to confidentiality</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Right to a fair and just tax system</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Right to finality (audits must eventually end)</span>
              </div>
            </div>
          </section>

          <section className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
            <AlertTriangle className="h-8 w-8 text-destructive mb-3" />
            <h2 className="text-2xl font-bold mb-4">Don't Ignore IRS Notices</h2>
            <p className="text-muted-foreground mb-4">
              Ignoring IRS correspondence makes everything worse. Penalties and interest continue to accrue, and the IRS can take aggressive collection action including:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Wage garnishment</li>
              <li>• Bank account levies</li>
              <li>• Federal tax liens on property</li>
              <li>• Seizure of assets</li>
              <li>• Passport revocation for seriously delinquent tax debt</li>
            </ul>
            <p className="text-muted-foreground mt-4 font-semibold">
              Contact us immediately upon receiving any IRS notice. Early intervention leads to better outcomes.
            </p>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">IRS Audit or Tax Dispute?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Don't face the IRS alone. Contact us today for experienced tax litigation representation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default TaxLitigation;
